import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import axiosPost from '../../../helpers/axios-helper';
import config from '../../../constants/config';
import ArticleEditor from '../../../components/editor/ArticleEditor';
import {
  parseBlocks, serializeBlocks, blocksToHtml,
  reviewDateToInput, reviewDateToUnix,
} from './ReviewEditor';
import './FeatureEditor.css';

/**
 * The editor for a venue's review, interview and menu.
 *
 * One component for all three and for all eight venue types: they are the same
 * shape of thing — a headline, an opening line, a body, and the highlight
 * boxes beside it. Only the wording and the menu's PDF differ, which the KINDS
 * table below describes rather than three near-identical editors.
 *
 * Nothing can be edited until the venue itself has been saved, because a
 * feature is stored against the venue's id.
 */

const KINDS = [
  {
    key: 'review',
    label: 'Review',
    blurb: 'What OutCaribbean thought of this place.',
    headlinePlaceholder: 'e.g. A grand old lady that still knows how to throw a party',
    defaultHighlight: 'What we love',
  },
  {
    key: 'interview',
    label: 'Interview',
    blurb: 'A conversation with the people behind it.',
    headlinePlaceholder: 'e.g. "We built this room for our mother"',
    defaultHighlight: 'In their words',
  },
  {
    key: 'menu',
    label: 'Menu',
    blurb: 'What they serve. Upload the PDF, and add anything worth calling out.',
    headlinePlaceholder: 'e.g. Dinner, served from six',
    defaultHighlight: 'Signature dishes',
  },
];

const parseList = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(v => String(v).trim()).filter(Boolean);
  return String(val).split(/\r?\n/).map(v => v.trim()).filter(Boolean);
};

const hasText = (html) => !!(html && String(html).replace(/<[^>]*>/g, '').trim());

const emptyFeature = (kind) => ({
  kind,
  title: '',
  standfirst: '',
  blocks: '',
  image: '',
  file_url: '',
  file_name: '',
  highlight_title: KINDS.find(k => k.key === kind)?.defaultHighlight || 'What we love',
  highlight_items: '',
  notes_title: 'Good to know',
  notes_items: '',
  author: '',
  published_at: '',
  active: 0,
});

/** An add/remove list, matching the amenities picker elsewhere in the form. */
function BulletList({ label, hint, placeholder, value, onChange }) {
  const items = parseList(value);
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v || items.includes(v)) { setDraft(''); return; }
    onChange([...items, v].join('\n'));
    setDraft('');
  };

  const move = (index, delta) => {
    const next = [...items];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next.join('\n'));
  };

  return (
    <div className="DashboardFormGroup">
      <label className="DashboardFormLabel">{label}</label>
      {hint && <p className="FeHint">{hint}</p>}

      <div className="FeBulletRow">
        <input
          type="text"
          className="DashboardFormInput"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className="DashboardButtonSecondary" onClick={add}>Add</button>
      </div>

      {items.length > 0 && (
        <ul className="FeBullets">
          {items.map((item, i) => (
            <li key={item}>
              <span>{item}</span>
              <span className="FeBulletControls">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down">↓</button>
                <button
                  type="button"
                  className="is-remove"
                  onClick={() => onChange(items.filter(x => x !== item).join('\n'))}
                  aria-label={`Remove ${item}`}
                >×</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FeatureEditor({ itemType, itemId, itemTitle = '', publicPath = '' }) {
  const [activeKind, setActiveKind] = useState('review');
  const [features, setFeatures] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [writingBody, setWritingBody] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfRef = useRef(null);

  const spec = KINDS.find(k => k.key === activeKind) || KINDS[0];
  const feature = features[activeKind] || emptyFeature(activeKind);

  const load = useCallback(() => {
    if (!itemId) { setIsLoading(false); return; }
    axiosPost(
      'features/for-item',
      { 'Content-Type': 'application/json' },
      { item_type: itemType, item_id: itemId, include_drafts: true },
      (resp) => {
        if (resp?.success) {
          const map = {};
          (resp.data.features || []).forEach((f) => {
            map[f.kind] = { ...f, published_at: reviewDateToInput(f.published_at) };
          });
          setFeatures(map);
          setError('');
        } else {
          setError(resp?.response || 'Could not load this venue\'s content.');
        }
        setIsLoading(false);
      }
    );
  }, [itemType, itemId]);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => {
    setFeatures(prev => ({
      ...prev,
      [activeKind]: { ...(prev[activeKind] || emptyFeature(activeKind)), ...patch },
    }));
    setMessage('');
  };

  const bodyWritten = hasText(feature.content) || parseBlocks(feature.blocks).length > 0;
  const headline = (feature.title || '').trim();
  const standfirst = (feature.standfirst || '').trim();
  const hasPdf = !!feature.file_url;

  // What still has to happen before this is worth publishing
  const missing = [];
  if (!headline) missing.push('a headline');
  if (!bodyWritten && !standfirst && !(activeKind === 'menu' && hasPdf)) {
    missing.push(activeKind === 'menu' ? 'a PDF or some text' : 'an opening line or a body');
  }

  const save = () => {
    if (isSaving) return;
    setIsSaving(true);
    setError('');
    setMessage('');

    const blocks = serializeBlocks(feature.blocks);
    const payload = {
      item_type: itemType,
      item_id: itemId,
      kind: activeKind,
      title: feature.title || '',
      standfirst: feature.standfirst || '',
      blocks,
      // The page renders this; it is produced from the blocks on every save
      content: parseBlocks(feature.blocks).length
        ? blocksToHtml(feature.blocks)
        : (feature.content || ''),
      image: feature.image || '',
      file_url: feature.file_url || '',
      file_name: feature.file_name || '',
      highlight_title: feature.highlight_title || spec.defaultHighlight,
      highlight_items: feature.highlight_items || '',
      notes_title: feature.notes_title || 'Good to know',
      notes_items: feature.notes_items || '',
      author: feature.author || '',
      published_at: reviewDateToUnix(feature.published_at),
      active: feature.active ? 1 : 0,
    };

    axiosPost('features/update', { 'Content-Type': 'application/json' }, payload, (resp) => {
      setIsSaving(false);
      if (resp?.success) {
        setMessage(resp.response || 'Saved.');
        load();
      } else {
        setError(resp?.response || 'Saving failed. Nothing was changed.');
      }
    });
  };

  const removeFeature = () => {
    if (!window.confirm(`Delete this ${spec.label.toLowerCase()}? This cannot be undone.`)) return;
    axiosPost(
      'features/delete',
      { 'Content-Type': 'application/json' },
      { item_type: itemType, item_id: itemId, kind: activeKind },
      (resp) => {
        if (resp?.success) {
          setFeatures(prev => {
            const next = { ...prev };
            delete next[activeKind];
            return next;
          });
          setMessage('Deleted.');
        } else {
          setError(resp?.response || 'Could not delete that.');
        }
      }
    );
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploadingPdf(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${config.api.url}features/upload-file`, fd, {
        headers: { api: config.api.key, 'Content-Type': 'multipart/form-data' },
      });
      if (resp?.data?.success) {
        set({ file_url: resp.data.data.url, file_name: resp.data.data.name });
      } else {
        setError(resp?.data?.response || 'The upload was refused.');
      }
    } catch (err) {
      setError('Could not upload that file.');
    } finally {
      setUploadingPdf(false);
    }
  };

  if (!itemId) {
    return (
      <section className="FePanel">
        <h3 className="FeTitle">Review, interview and menu</h3>
        <p className="FeHint">Save this item first — these are stored against it.</p>
      </section>
    );
  }

  // The block editor is rendered through a portal, not inline. This panel sits
  // inside the venue's own <form>, and ArticleEditor brings its own <form> with
  // a submit button: nested forms are flattened by the browser, so its "Done"
  // was submitting the venue form instead — saving the venue, closing the
  // editor, and losing whatever had just been written.
  const bodyEditor = writingBody
    ? createPortal(
        <div
          className="FeBodyOverlay"
          // A portal moves the DOM node but not the React tree: the block
          // editor's submit still bubbles to the venue form it is nested
          // under, which saved the venue and closed the whole editor. The
          // boundary is stopped here, where the two trees part company.
          onSubmit={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <ArticleEditor
            mode="review"
            initialData={{ blocks: parseBlocks(feature.blocks) }}
            onSave={(data) => {
              set({ blocks: serializeBlocks(data.blocks) });
              setWritingBody(false);
            }}
            onClose={() => setWritingBody(false)}
          />
        </div>,
        document.body
      )
    : null;

  return (
    <section className="FePanel">
      {bodyEditor}
      <header className="FePanelHead">
        <div>
          <h3 className="FeTitle">Review, interview and menu</h3>
          <p className="FeHint">
            Each gets its own page, linked from {itemTitle || 'this item'}&rsquo;s page.
          </p>
        </div>
      </header>

      <div className="FeTabs" role="tablist">
        {KINDS.map((k) => {
          const existing = features[k.key];
          const live = existing && Number(existing.active) === 1;
          return (
            <button
              key={k.key}
              type="button"
              role="tab"
              aria-selected={activeKind === k.key}
              className={`FeTab ${activeKind === k.key ? 'is-active' : ''}`}
              onClick={() => { setActiveKind(k.key); setMessage(''); setError(''); }}
            >
              {k.label}
              <span className={`FeTabDot ${live ? 'is-live' : existing ? 'is-draft' : ''}`} />
            </button>
          );
        })}
      </div>

      <p className="FeBlurb">{spec.blurb}</p>

      <div className="FeStatus">
        <span className={`FePill ${feature.active ? 'is-live' : 'is-draft'}`}>
          {feature.active ? 'Live on the site' : 'Draft — nobody can see it'}
        </span>
        <label className="FeSwitch">
          <input
            type="checkbox"
            checked={!!feature.active}
            onChange={(e) => set({ active: e.target.checked ? 1 : 0 })}
          />
          <span className="FeSwitchTrack" aria-hidden="true"><span className="FeSwitchKnob" /></span>
          <span>Publish</span>
        </label>
      </div>

      {!!feature.active && missing.length > 0 && (
        <p className="FeWarning">
          This is set to publish but still needs {missing.join(' and ')}. Until then
          it stays hidden.
        </p>
      )}

      {!!feature.active && missing.length === 0 && publicPath && (
        <p className="FeLive">
          Live at <a href={`${publicPath}/${activeKind}`} target="_blank" rel="noreferrer">
            {publicPath}/{activeKind}
          </a>
        </p>
      )}

      {error && <p className="FeError" role="alert">{error}</p>}
      {message && !error && <p className="FeOk">{message}</p>}

      {isLoading ? (
        <p className="FeHint">Loading...</p>
      ) : (
        <>
          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Headline</label>
            <input
              type="text"
              className="DashboardFormInput"
              maxLength={255}
              value={feature.title || ''}
              onChange={(e) => set({ title: e.target.value })}
              placeholder={spec.headlinePlaceholder}
            />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Opening line</label>
            <p className="FeHint">
              One or two sentences. Sits under the headline, and is the teaser on
              the item&rsquo;s own page.
            </p>
            <textarea
              className="DashboardFormInput"
              rows={3}
              maxLength={500}
              value={feature.standfirst || ''}
              onChange={(e) => set({ standfirst: e.target.value })}
            />
          </div>

          {activeKind === 'menu' && (
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Menu PDF</label>
              <p className="FeHint">Up to 20MB. Readers open it in a new tab.</p>

              {hasPdf && (
                <p className="FeFile">
                  <a href={feature.file_url} target="_blank" rel="noreferrer">
                    {feature.file_name || 'View the PDF'}
                  </a>
                  <button type="button" onClick={() => set({ file_url: '', file_name: '' })}>
                    Remove
                  </button>
                </p>
              )}

              <button
                type="button"
                className="DashboardButtonSecondary"
                onClick={() => pdfRef.current?.click()}
                disabled={uploadingPdf}
              >
                {uploadingPdf ? 'Uploading...' : (hasPdf ? 'Replace PDF' : 'Upload PDF')}
              </button>
              <input
                ref={pdfRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => { uploadPdf(e.target.files[0]); e.target.value = ''; }}
              />
            </div>
          )}

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">
              {activeKind === 'menu' ? 'Anything to say about it' : 'The body'}
            </label>
            <p className="FeHint">
              Written in the same editor as an article: text, photos and video as
              blocks you can drag into order.
            </p>
            <div className="FeBodyRow">
              <span className="FeBodyCount">
                {bodyWritten
                  ? `${parseBlocks(feature.blocks).length || 1} block${parseBlocks(feature.blocks).length === 1 ? '' : 's'}`
                  : 'Nothing written yet'}
              </span>
              <button type="button" className="DashboardButton" onClick={() => setWritingBody(true)}>
                {bodyWritten ? 'Edit' : 'Write'}
              </button>
            </div>
          </div>

          {/* The highlight boxes. Their headings are editable, which is the
              point: "What we love" suits a review, not a menu. */}
          <div className="FeBoxes">
            <div className="FeBox">
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">First box heading</label>
                <input
                  type="text"
                  className="DashboardFormInput"
                  maxLength={255}
                  value={feature.highlight_title ?? spec.defaultHighlight}
                  onChange={(e) => set({ highlight_title: e.target.value })}
                  placeholder={spec.defaultHighlight}
                />
              </div>
              <BulletList
                label="Points"
                hint="Short lines. Press Enter or Add after each one."
                placeholder="e.g. The rooftop pool at sunset"
                value={feature.highlight_items}
                onChange={(v) => set({ highlight_items: v })}
              />
            </div>

            <div className="FeBox">
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Second box heading</label>
                <input
                  type="text"
                  className="DashboardFormInput"
                  maxLength={255}
                  value={feature.notes_title ?? 'Good to know'}
                  onChange={(e) => set({ notes_title: e.target.value })}
                  placeholder="Good to know"
                />
              </div>
              <BulletList
                label="Points"
                hint="Leave empty to hide this box entirely."
                placeholder="e.g. No lift to the upper floors"
                value={feature.notes_items}
                onChange={(v) => set({ notes_items: v })}
              />
            </div>
          </div>

          <div className="FeGrid">
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">By</label>
              <input
                type="text"
                className="DashboardFormInput"
                value={feature.author || ''}
                onChange={(e) => set({ author: e.target.value })}
                placeholder="e.g. OutCaribbean"
              />
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Date</label>
              <input
                type="date"
                className="DashboardFormInput"
                value={feature.published_at || ''}
                onChange={(e) => set({ published_at: e.target.value })}
              />
            </div>
          </div>

          <div className="FeActions">
            {features[activeKind] && (
              <button type="button" className="FeDelete" onClick={removeFeature}>
                Delete this {spec.label.toLowerCase()}
              </button>
            )}
            <button type="button" className="DashboardButton" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving...' : `Save ${spec.label.toLowerCase()}`}
            </button>
          </div>

          <p className="FeHint FeFootnote">
            This saves on its own, separately from the item&rsquo;s own Save button.
          </p>
        </>
      )}
    </section>
  );
}
